/* HAND-AUTHORED: Primerry search drawer history and predictive suggestions. */

const STORAGE_KEY = 'primerry-search-history';
const MAX_RECENT_SEARCHES = 6;
const SEARCH_DEBOUNCE_MS = 200;

class PrimerrySearchDrawer {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-primerry-search-drawer]');
    this.input = root.querySelector('[data-primerry-search-input]');
    this.clearButton = root.querySelector('[data-primerry-search-clear]');
    this.chips = root.querySelector('[data-primerry-search-chips]');
    this.tiles = root.querySelector('[data-primerry-search-tiles]');
    this.heading = root.querySelector('[data-primerry-suggestions-heading]');
    this.defaultTiles = this.tiles?.innerHTML || '';
    // Chips and tiles are rebuilt as the shopper types; announce them politely.
    this.chips?.setAttribute('aria-live', 'polite');
    this.tiles?.setAttribute('aria-live', 'polite');
    this.timer = null;
    this.requestId = 0;
    this.bind();
  }

  bind() {
    this.root.addEventListener('dialog:open', () => {
      this.renderChips();
      window.requestAnimationFrame(() => this.input?.focus());
    });
    this.form?.addEventListener('submit', () => this.saveSearch(this.input?.value));
    this.input?.addEventListener('input', () => this.onInput());
    this.clearButton?.addEventListener('click', () => this.clearSearch());
    this.chips?.addEventListener('click', (event) => this.onChipClick(event));
  }

  getFallbackSearches() {
    return (this.form?.dataset.fallbackSearches || '').split(',').map((term) => term.trim()).filter(Boolean);
  }

  getRecentSearches() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string' && term.trim()) : [];
    } catch (_) {
      return [];
    }
  }

  saveSearch(value) {
    const term = value?.trim();
    if (!term) return;
    try {
      const searches = [term, ...this.getRecentSearches().filter((search) => search.toLowerCase() !== term.toLowerCase())];
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES)));
    } catch (_) {
      // Storage is optional in private or restricted browser contexts.
    }
  }

  renderChips() {
    if (!this.chips) return;
    const searches = this.getRecentSearches();
    const terms = searches.length ? searches : this.getFallbackSearches();
    this.chips.replaceChildren(...terms.map((term) => this.createChip(term)));
  }

  createChip(term) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'primerry-search-modal__chip';
    chip.dataset.primerrySearchTerm = term;
    chip.textContent = term;
    return chip;
  }

  onChipClick(event) {
    const chip = event.target.closest('[data-primerry-search-term]');
    if (!chip || !this.input) return;
    this.input.value = chip.dataset.primerrySearchTerm || '';
    this.saveSearch(this.input.value);
    this.form?.requestSubmit();
  }

  onInput() {
    const query = this.input?.value.trim() || '';
    if (this.clearButton) this.clearButton.hidden = !query;
    window.clearTimeout(this.timer);
    if (!query) {
      this.restoreSuggestions();
      return;
    }
    this.timer = window.setTimeout(() => this.fetchSuggestions(query), SEARCH_DEBOUNCE_MS);
  }

  clearSearch() {
    if (!this.input) return;
    this.input.value = '';
    this.input.focus();
    this.onInput();
  }

  async fetchSuggestions(query) {
    const requestId = ++this.requestId;
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      if (requestId !== this.requestId || this.input?.value.trim() !== query) return;
      this.renderSuggestions(data?.resources?.results?.products || [], query);
    } catch (_) {
      // The static suggestions remain available when predictive search is unavailable.
    }
  }

  renderSuggestions(products, query) {
    if (!this.tiles || !this.heading) return;
    this.heading.textContent = products.length ? 'Suggestions' : `No products found for “${query}”`;
    this.tiles.replaceChildren(...products.map((product) => this.createTile(product)));
  }

  createTile(product) {
    const tile = document.createElement('a');
    tile.className = 'primerry-search-modal__tile';
    tile.href = product.url || '#';
    const image = document.createElement('span');
    image.className = 'primerry-search-modal__tile-image';
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title || '';
      img.loading = 'lazy';
      image.append(img);
    }
    const title = document.createElement('span');
    title.className = 'primerry-search-modal__tile-title';
    title.textContent = product.title || '';
    tile.append(image, title);
    return tile;
  }

  restoreSuggestions() {
    if (!this.tiles || !this.heading) return;
    this.requestId += 1;
    this.heading.textContent = 'Suggestions';
    this.tiles.innerHTML = this.defaultTiles;
  }
}

function initPrimerrySearchDrawers(scope = document) {
  scope.querySelectorAll?.('#search-modal').forEach((root) => {
    if (!root.dataset.primerrySearchDrawerReady) {
      root.dataset.primerrySearchDrawerReady = 'true';
      new PrimerrySearchDrawer(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPrimerrySearchDrawers(), { once: true });
} else {
  initPrimerrySearchDrawers();
}
